import { Component, computed, inject, input } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { EPlatform, EPlatformFeature } from '@enums/campaign.enum';
import { TranslateService } from '@ngx-translate/core';
import { IconBadgeComponent } from '@shared/components/icon-badge/icon-badge.component';
import { SocialBadgeComponent } from '@shared/components/social-badge/social-badge.component';
import { camelCase } from 'lodash';
import { IBadgeData, TPlatform } from './social-badge-list.interface';

@Component({
  imports: [SocialBadgeComponent, IconBadgeComponent, MatTooltipModule],
  selector: 'app-social-badge-list',
  styleUrl: './social-badge-list.component.scss',
  templateUrl: './social-badge-list.component.html',
})
export class SocialBadgeListComponent {
  public readonly badgeData = input.required<IBadgeData[]>();

  public readonly size = input<number>(24);
  public readonly iconSize = input<number>(16);
  public readonly borderWidth = input<number>(0);
  public readonly backgroundColor = input<string>('transparent');
  public readonly shownIconsCount = input<number>(3);
  public readonly showTooltip = input<boolean>(false);

  public readonly displayedIconsCount = computed(() => {
    const totalIcons = this.badgeData().length;
    const maxShown = this.shownIconsCount();

    if (totalIcons <= maxShown) {
      return totalIcons;
    }

    return maxShown - 1;
  });

  public readonly moreIconsCount = computed(() => {
    const totalIcons = this.badgeData().length;
    const displayed = this.displayedIconsCount();
    const remaining = totalIcons - displayed;

    if (remaining <= 0) {
      return '';
    }

    return `+${remaining}`;
  });

  public readonly moreIconsTooltip = computed(() =>
    this.badgeData()
      .slice(this.displayedIconsCount())
      .map((badge) => this.translate.instant(`shared.platformFeatures.${camelCase(badge.platform)}`))
      .join(',\n')
      .trim(),
  );

  public readonly hasMoreIcons = computed(() => {
    const totalIcons = this.badgeData().length;
    const displayed = this.displayedIconsCount();

    return totalIcons > displayed;
  });

  public readonly moreIconsCountFontSize = computed(() => {
    if (this.size() === 40) {
      return 14;
    }

    if (this.size() === 32) {
      return 12;
    }

    if (this.size() === 16) {
      return 8;
    }

    return 10;
  });

  public readonly EPlatform = EPlatform;

  private readonly translate = inject(TranslateService);

  public isYoutube(platform: TPlatform): boolean {
    const platformFormatted = camelCase(platform) as TPlatform;

    return (
      platformFormatted === EPlatform.youtube ||
      platformFormatted === EPlatformFeature.youtubeStream ||
      platformFormatted === EPlatformFeature.youtubeVod
    );
  }
}
